import { Table } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { useTableSearch } from '../../../hooks/useTableSearch';
import { getColumns } from './columns';
import TableActions from './TabelActions';
import { OrderByDirection } from 'firebase/firestore';
import { useSearchableFilter } from '../../../hooks/useSearchableFilter';
import { GenerationStatus } from '../../../constants/status';
import { Company } from '../../../store/companySlice';
import { CustomProduct, fetchCustomProducts, resetCustomProducts, setCustomProductPagination, setSelectedCustomProduct } from '../../../store/customProductSlice';
import CustomProductDetailModal from '../CustomProductDetailModal';
import UploadCustomProductsModal from '../UploadCustomProductsModal';
import DeleteCustomProductsModal from '../DeleteCustomProductsModal';
import { CustomProductBulkTarget } from '../../../types/customProductBulkActions';


interface CustomProductTableProps {
    openImages?: () => void
}


const CustomProductTable: React.FC<CustomProductTableProps> = ({ openImages }) => {
    const dispatch = useAppDispatch()

    const selectedCompany = useAppSelector((state) => state.companies.selectedCompany)
    const customProducts = useAppSelector((state) => state.customProducts.customProducts)
    const selectedCustomProduct = useAppSelector((state) => state.customProducts.selectedCustomProduct)

    const [productDetailModalOpen, setProductDetailModalOpen] = useState<boolean>(false)
    const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false)
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)

    const [isLoading, setIsLoading] = useState(false);
    const activeFilters = useAppSelector((state) => state.customProducts.activeFilters)
    const activeSorter = useAppSelector((state) => state.customProducts.activeSorter)
    const pageSize = useAppSelector((state) => state.customProducts.pagination.pageSize)
    const lastVisibleDoc = useAppSelector((state) => state.customProducts.pagination.lastVisibleDoc)
    const hasMore = useAppSelector((state) => state.customProducts.pagination.hasMore)
    const [currentCompany, setCurrentCompany] = useState<Company | null>(null)
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]); const { getColumnSearchProps } = useTableSearch<CustomProduct>();

    useEffect(() => {
        if (!selectedCompany) {
            dispatch(resetCustomProducts());
            return;
        }

        if (selectedCompany.id !== currentCompany?.id) {
            console.warn("Shopify Shop changed");
            setCurrentCompany(selectedCompany);
            setSelectedRowKeys([]);
            resetShop();
        }
    }, [selectedCompany]);

    //Track updates to customProducts and update selectedCustomProduct if necessary
    useEffect(() => {
        if (customProducts && selectedCustomProduct) {
            const updatedEntity = customProducts.find((sp) => sp.id === selectedCustomProduct.id);
            if (updatedEntity) {
                dispatch(setSelectedCustomProduct(updatedEntity));
            }
        }
    }, [customProducts, dispatch])


    useEffect(() => {
        return () => {
            dispatch(resetCustomProducts())
        }
    }, [dispatch])

    const resetShop = async (activeSorter?: {
        field: string;
        order: OrderByDirection;
    } | null, activeFilters?: Record<string, any>) => {
        setIsLoading(true);
        dispatch(resetCustomProducts());

        // Reset scroll position to top
        const table = document.querySelector('.ant-table-body') as HTMLElement;
        if (table) table.scrollTop = 0;

        if (selectedCompany) {
            console.log("activeFilters", activeFilters)
            await dispatch(
                fetchCustomProducts({
                    companyId: selectedCompany.id,
                    pageSize,
                    lastVisibleDoc: null,
                    activeSorter: activeSorter, // Add sortBy if needed
                    activeFilters: activeFilters ? activeFilters : {}, // Add active filters if needed
                    refetch: true
                })
            );
        }
        setIsLoading(false)
    }

    const fetchMoreData = async (activeSorter?: { field: string; order: OrderByDirection } | null, activeFilters?: Record<string, any>) => {


        if (!selectedCompany || isLoading || !hasMore) {
            console.log("no shop or loading or no more data")
            console.log({ selectedAmazonCompany: selectedCompany, isLoading, hasMore })
            return;
        }

        setIsLoading(true);

        await dispatch(
            fetchCustomProducts({
                companyId: selectedCompany.id,
                pageSize,
                lastVisibleDoc,
                activeSorter: activeSorter, // Add sortBy if needed
                activeFilters: activeFilters ? activeFilters : {}, // Add active filters if needed
                refetch: false
            })
        );

        setIsLoading(false);
    };

    const refetchCustomProducts = async (
        companyId: string,
        filters?: Record<string, any>,
        sorter?: {
            field: any;
            order: OrderByDirection;
        } | null,
    ) => {
        setIsLoading(true)
        dispatch(setCustomProductPagination({
            page: 1,
            pageSize,
            lastVisibleDoc: null,
            hasMore: true
        }));
        const table = document.querySelector('.ant-table-body') as HTMLElement;
        if (table) table.scrollTop = 0;
        await dispatch(
            fetchCustomProducts({
                companyId: companyId,
                pageSize,
                lastVisibleDoc: null,
                activeSorter: sorter, // Add sortBy if needed
                activeFilters: filters ? filters : {}, // Add active filters if needed
                refetch: true
            })
        );
        setIsLoading(false)
    }

    const handleTableChange = async (
        pagination: TablePaginationConfig,
        filters: Record<string, any>,
        sorter: any
    ) => {

        console.log("handleTableChange:", {
            filters,
            sorter
        })

        const hasFiltersChanged = JSON.stringify(activeFilters) !== JSON.stringify(filters);

        if (hasFiltersChanged || sorter?.field) {
            setSelectedRowKeys([]);
            const order = sorter.order === "ascend" ? "asc" : "desc" as OrderByDirection;
            const tempSorter = sorter.field ? { field: sorter.field, order } : null

            console.log("filters changed")
            if (selectedCompany) {
                await refetchCustomProducts(
                    selectedCompany.id,
                    filters ? filters : {},
                    tempSorter
                )
            }
        }
    };

    const handleRefreshProducts = async () => {
        if (selectedCompany) {
            await refetchCustomProducts(
                selectedCompany.id,
                activeFilters,
                activeSorter
            )
        }
    }

    const handleProductDetailModalOpen = (product: CustomProduct) => {
        dispatch(setSelectedCustomProduct(product))
        setProductDetailModalOpen(true)
    }

    const handleProductDetailModalClose = () => {
        setProductDetailModalOpen(false)
        dispatch(setSelectedCustomProduct(null))
    }


    const generationStatusFilters = Object.values(GenerationStatus).map((status) => ({
        text: status,
        value: status,
    }));


    const brandFilter = useMemo(() => {
        const unique = selectedCompany?.customProductBrands ?? [];
        return unique.map((u) => ({
            text: u,
            value: u,
        }));
    }, [selectedCompany])

    const marketplaceFilter = useMemo(() => {
        const marketplaces = Array.from(
            new Set(customProducts.map((product) => product.input?.marketplace).filter(Boolean))
        ) as string[];

        return marketplaces.sort().map((marketplace) => ({
            text: marketplace,
            value: marketplace,
        }));
    }, [customProducts]);

    const marketplaceFilterProps = useSearchableFilter<CustomProduct>(['input', 'marketplace'], marketplaceFilter)();
    const brandFilterProps = useSearchableFilter<CustomProduct>(['input', 'brand'], brandFilter)();

    const columns = getColumns({
        getColumnSearchProps,
        handleProductDetailModalOpen,
        marketplaceFilterProps,
        brandFilterProps,
        generationStatusFilters
    })


    const debounce = (func: Function, wait: number) => {
        let timeout: NodeJS.Timeout;
        return (...args: any[]) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), wait);
        };
    };

    const handleScroll = ({ target }: any) => {
        const { scrollTop, scrollHeight, clientHeight } = target as HTMLElement;
        if (scrollTop + clientHeight >= scrollHeight - 50) {
            fetchMoreData(activeSorter, activeFilters);
        }
    };

    // Debounce the scroll handler to prevent rapid triggering
    const debounceScroll = debounce(handleScroll, 200);

    const hasActiveFilters = useMemo(() => {
        return Object.values(activeFilters || {}).some((value) => {
            if (Array.isArray(value)) return value.length > 0;
            return value !== null && value !== undefined && value !== '';
        });
    }, [activeFilters]);

    const bulkTarget = useMemo<CustomProductBulkTarget>(() => {
        const productIds = selectedRowKeys.map(String);
        if (productIds.length > 0) {
            return {
                scope: 'selected',
                productIds,
            };
        }
        if (hasActiveFilters) {
            return {
                scope: 'filtered',
                filters: activeFilters,
            };
        }
        return { scope: 'all' };
    }, [activeFilters, hasActiveFilters, selectedRowKeys]);

    return (
        <div className="custom-product-generator-table-shell">
            <Table
                key={selectedCompany?.id}
                className="custom-product-main-table"
                columns={columns}
                dataSource={customProducts}
                rowKey="id"
                size='small'
                bordered
                pagination={false}
                onChange={handleTableChange}
                loading={isLoading && customProducts.length === 0}
                rowSelection={{
                    selectedRowKeys,
                    preserveSelectedRowKeys: true,
                    onChange: setSelectedRowKeys,
                }}
                title={() => (
                    <div className="custom-product-table-toolbar">
                        <TableActions
                            selectedCompany={selectedCompany}
                            selectedRowKeys={selectedRowKeys}
                            bulkTarget={bulkTarget}
                            hasActiveFilters={hasActiveFilters}
                            setUploadModalOpen={setUploadModalOpen}
                            setDeleteModalOpen={setDeleteModalOpen}
                            handleRefreshProducts={handleRefreshProducts}
                        />
                    </div>
                )}
                scroll={{ x: 'max-content', y: 'calc(100vh - 348px)', scrollToFirstRowOnChange: true }}
                onScroll={debounceScroll}

            />
            <CustomProductDetailModal
                open={productDetailModalOpen}
                onClose={handleProductDetailModalClose}
            />
            <UploadCustomProductsModal
                open={uploadModalOpen}
                onClose={() => setUploadModalOpen(false)}
            />
            <DeleteCustomProductsModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                bulkTarget={bulkTarget}
                selectedCount={selectedRowKeys.length}
                hasActiveFilters={hasActiveFilters}
            />
        </div >
    )
}

export default CustomProductTable
